<script lang="ts">
  import { onMount, onDestroy } from "svelte"

  type OrgUsageRow = {
    id: number
    name: string
    tokens: number
    cost_cents: number
    users: number
  }

  let { rows = [] }: { rows: OrgUsageRow[] } = $props()

  type UsageTheme = {
    accent: string
    border: string
    textSecondary: string
    textStrong: string
  }

  const fallbackTheme: UsageTheme = {
    accent: "#3E6AE1",
    border: "#E3E5E8",
    textSecondary: "#5F6368",
    textStrong: "#171A20"
  }

  const themeColor = (name: string, fallback: string) => {
    const css = getComputedStyle(document.documentElement)
    return css.getPropertyValue(name).trim() || fallback
  }

  const resolveUsageTheme = (): UsageTheme => ({
    accent: themeColor("--accent", fallbackTheme.accent),
    border: themeColor("--border", fallbackTheme.border),
    textSecondary: themeColor("--text-secondary", fallbackTheme.textSecondary),
    textStrong: themeColor("--text-strong", fallbackTheme.textStrong)
  })

  let usageTheme = $state<UsageTheme>(fallbackTheme)
  let themeObserver: MutationObserver | null = null

  const maxTokens = $derived(Math.max(1, ...rows.map((row) => row.tokens || 0)))

  const formatTokens = (value: number) => new Intl.NumberFormat("ko-KR").format(value)

  const applyUsageTheme = () => {
    usageTheme = resolveUsageTheme()
  }

  onMount(() => {
    applyUsageTheme()

    themeObserver = new MutationObserver(applyUsageTheme)
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"]
    })
  })

  onDestroy(() => {
    themeObserver?.disconnect()
    themeObserver = null
  })
</script>

<div class="space-y-4">
  {#if rows.length > 0}
    {#each rows as row (row.id)}
      <div class="grid gap-3 md:grid-cols-[180px_1fr_96px] md:items-center">
        <div>
          <p class="text-sm font-medium" style={`color: ${usageTheme.textStrong};`}>{row.name}</p>
          <p class="mt-1 text-xs" style={`color: ${usageTheme.textSecondary};`}>사용자 {formatTokens(row.users)}명</p>
        </div>
        <div
          class="h-3 overflow-hidden rounded-[4px]"
          style={`background-color: ${usageTheme.border};`}
          aria-label={`${row.name} 이번 달 토큰 ${formatTokens(row.tokens)}`}
        >
          <div
            class="h-full rounded-[4px]"
            style={`width: ${Math.max(2, (row.tokens / maxTokens) * 100)}%; background-color: ${usageTheme.accent};`}
          ></div>
        </div>
        <p class="text-right text-sm font-medium" style={`color: ${usageTheme.textStrong};`}>{formatTokens(row.tokens)}</p>
      </div>
    {/each}
  {:else}
    <p class="text-sm" style={`color: ${usageTheme.textSecondary};`}>아직 표시할 조직 사용량이 없습니다.</p>
  {/if}
</div>
